import React from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Lead, FunnelStage } from '../../types'
import { KanbanCard } from './KanbanCard'

interface KanbanColumnProps {
  stage: FunnelStage
  stageIndex: number
  leads: Lead[]
  isFirst: boolean
  isLast: boolean
  onViewLead?: (lead: Lead) => void
  onEditLead: (lead: Lead) => void
  onMoveLeadForward: (lead: Lead) => void
  onMoveLeadBackward: (lead: Lead) => void
}

export const KanbanColumn: React.FC<KanbanColumnProps> = ({
  stage,
  stageIndex,
  leads,
  isFirst,
  isLast,
  onViewLead,
  onEditLead,
  onMoveLeadForward,
  onMoveLeadBackward,
}) => {
  const { t } = useTranslation()
  const [draggedOver, setDraggedOver] = React.useState(false)

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setDraggedOver(true)
  }

  const handleDragLeave = () => {
    setDraggedOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDraggedOver(false)

    const leadData = e.dataTransfer.getData('lead')
    if (!leadData) return
    let lead: Lead & { sourceStageIndex?: number }
    try {
      lead = JSON.parse(leadData)
    } catch {
      return
    }
    const sourceIdx =
      typeof lead.sourceStageIndex === 'number' ? lead.sourceStageIndex : -1
    if (sourceIdx < 0 || sourceIdx === stageIndex) return

    if (stageIndex === sourceIdx + 1) {
      onMoveLeadForward(lead)
    } else if (stageIndex === sourceIdx - 1) {
      onMoveLeadBackward(lead)
    }
  }

  return (
    <div className="flex-shrink-0 w-72">
      <div className="bg-card border border-border rounded-xl p-4 flex flex-col gap-4 shadow-sm">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0 flex items-center gap-2">
            <span
              className="h-2.5 w-2.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: stage.color }}
            />
            <h3 className="font-semibold text-foreground text-sm truncate">
              {stage.name}
            </h3>
          </div>
          <span className="flex-shrink-0 text-xs font-semibold tabular-nums text-foreground bg-muted px-2 py-1 rounded-md">
            {leads.length}
          </span>
        </div>

        {stage.description && (
          <p className="text-xs text-muted-foreground leading-relaxed">{stage.description}</p>
        )}

        <div
          className={`flex-1 min-h-[180px] transition-all ${
            draggedOver
              ? 'bg-accent/5 border border-dashed border-accent/40 rounded-lg p-3'
              : ''
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {leads.length === 0 ? (
            <div className="flex items-center justify-center h-full min-h-[120px] bg-surface/50 rounded-lg border border-dashed border-border">
              <p className="text-xs text-muted-foreground">Nenhum lead</p>
            </div>
          ) : (
            <div className="space-y-3">
              {leads.map((lead) => (
                <div
                  key={lead.id}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = 'move'
                    e.dataTransfer.setData(
                      'lead',
                      JSON.stringify({ ...lead, sourceStageIndex: stageIndex }),
                    )
                  }}
                  onClick={() => onViewLead?.(lead)}
                  className="relative group cursor-grab active:cursor-grabbing"
                >
                  <KanbanCard lead={lead} onEdit={onEditLead} />

                  <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 flex justify-between opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                    {!isFirst && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          onMoveLeadBackward(lead)
                        }}
                        className="pointer-events-auto -ml-3 h-8 w-8 bg-card border border-border rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                        title={t('leads.kanban.moveToPreviousStage')}
                        type="button"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                    )}
                    {!isLast && (
                      <button
                        type="button"
                        disabled={!lead.assignedTo}
                        onClick={(e) => {
                          e.stopPropagation()
                          onMoveLeadForward(lead)
                        }}
                        className="pointer-events-auto -mr-3 h-8 w-8 bg-card border border-border rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors ml-auto disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-card"
                        title={
                          lead.assignedTo
                            ? t('leads.kanban.moveToNextStage')
                            : t('leads.kanban.nextStageRequiresAssignee')
                        }
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}